import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { 
  Building2, 
  Lock,
  User,
  Eye,
  EyeOff
} from "lucide-react";

const Login = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    username: "",
    password: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Check if already logged in
  useEffect(() => {
    const isAuthenticated = localStorage.getItem("eliteedge_auth") === "true";
    if (isAuthenticated) {
      navigate("/crm");
    }
  }, [navigate]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    // Simple authentication - in production, this would be server-side
    const validCredentials = {
      username: "admin",
      password: "EliteEdge2025!"
    };

    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    if (formData.username === validCredentials.username && 
        formData.password === validCredentials.password) {
      
      // Set authentication
      localStorage.setItem("eliteedge_auth", "true");
      localStorage.setItem("eliteedge_user", formData.username);
      localStorage.setItem("eliteedge_login_time", new Date().toISOString());

      toast({
        title: "Login Successful!",
        description: "Welcome to Elite Edge Management Consultancy CRM.",
      });

      // Redirect to intended page or CRM dashboard
      const from = location.state?.from?.pathname || "/crm";
      navigate(from, { replace: true });
    } else {
      toast({
        title: "Login Failed",
        description: "Invalid username or password. Please try again.",
        variant: "destructive"
      });
    }

    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <img 
                src="./images/logo.jpg" 
                alt="Company Logo" 
                className="h-12 w-auto object-contain"
              />
              <span className="text-2xl font-bold text-primary">Elite Edge MC</span>
            </Link>
            <div className="hidden md:flex items-center space-x-6">
              <Link to="/" className="text-foreground hover:text-primary transition-colors">Home</Link>
              <Link to="/services" className="text-foreground hover:text-primary transition-colors">Services</Link>
              <Link to="/client-form" className="text-foreground hover:text-primary transition-colors">Apply Now</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Login Form */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-md">
          <div className="text-center mb-8">
            <Badge className="mb-4" variant="secondary">Admin Access</Badge>
            <h1 className="text-4xl font-bold mb-4">CRM Login</h1>
            <p className="text-xl text-muted-foreground">
              Access your Elite Edge Management Consultancy CRM system
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-center">
                <Lock className="h-5 w-5 mr-2" />
                Secure Login
              </CardTitle>
              <CardDescription className="text-center">
                Enter your credentials to access the CRM dashboard
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="username">Username</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="username"
                      type="text"
                      value={formData.username}
                      onChange={(e) => handleInputChange("username", e.target.value)}
                      placeholder="Enter your username"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => handleInputChange("password", e.target.value)}
                      placeholder="Enter your password"
                      className="pl-10 pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-primary to-primary-glow"
                  disabled={isLoading}
                >
                  {isLoading ? "Signing In..." : "Sign In"}
                </Button>
              </form>


            </CardContent>
          </Card>

          {/* Security Notice */}
          <div className="mt-8 text-center">
            <p className="text-sm text-muted-foreground">
              🔒 Your session will automatically expire after 24 hours for security
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Login;